/**
 * Cloudflare R2 上传工具
 * 替代 UploadThing，提供兼容的 Hook 接口
 */

import { useState, useCallback } from 'react'
import { useAuth } from './db'
import { createSemaphore, callWithRetry } from './semaphore'

/** 上传并发控制：设置为 6 (对齐浏览器限制) */
const uploadSemaphore = createSemaphore(6)

/** 上传结果类型（兼容 UploadThing 返回格式） */
export interface R2UploadResult {
  url: string
  name: string
  key?: string
  /** 兼容 UploadThing 的 ufsUrl 字段 */
  ufsUrl: string
}

/** 预签名 URL 响应 */
interface PresignResponse {
  uploadUrl: string
  publicUrl: string
  key: string
}

/**
 * 获取预签名 URL（带 token 参数）
 */
async function getPresignedUrl(
  filename: string,
  contentType: string,
  token: string
): Promise<PresignResponse> {
  const response = await fetch('/api/r2-presign', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ filename, contentType }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.error || '获取上传地址失败')
  }

  return response.json()
}

/**
 * 上传单个文件到 R2（带 token 参数，带并发控制和重试）
 */
async function uploadFileToR2(file: File, token: string): Promise<R2UploadResult> {
  return await uploadSemaphore.acquire().then(async () => {
    try {
      // 使用 callWithRetry 增加稳定性
      return await callWithRetry(async () => {
        // 1. 获取预签名 URL
        const { uploadUrl, publicUrl, key } = await getPresignedUrl(
          file.name,
          file.type || 'application/octet-stream',
          token
        )

        // 2. 直接上传到 R2
        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type || 'application/octet-stream',
          },
        })

        if (!uploadResponse.ok) {
          throw new Error(`文件上传失败: ${uploadResponse.statusText} (${uploadResponse.status})`)
        }

        return {
          url: publicUrl,
          name: file.name,
          key,
          ufsUrl: publicUrl, // 兼容 UploadThing
        }
      }, {
        retries: 2, // 失败重试 2 次
        retryDelay: 1000,
        onRetry: (attempt, err) => {
          console.warn(`[R2] 文件 ${file.name} 上传第 ${attempt} 次失败，正在重试...`, err.message)
        }
      })
    } finally {
      // 无论成功失败，必须释放信号量
      uploadSemaphore.release()
    }
  })
}

/**
 * 批量上传文件到 R2（带 token 参数）
 */
export async function uploadToR2(files: File[], token: string): Promise<R2UploadResult[]> {
  const results = await Promise.all(files.map(file => uploadFileToR2(file, token)))
  return results
}

/** Hook 配置选项（兼容 UploadThing 签名） */
interface UseR2UploadOptions {
  onClientUploadComplete?: (res: R2UploadResult[]) => void
  onUploadError?: (error: Error) => void
}

/**
 * R2 上传 Hook
 * 兼容 useUploadThing 的接口签名
 * 
 * 使用 useAuth() 直接获取 refresh_token，避免从 IndexedDB 读取失败的问题
 * 
 * @example
 * // 原来的写法
 * const { startUpload, isUploading } = useUploadThing('assetUploader')
 * 
 * // 新的写法
 * const { startUpload, isUploading } = useR2Upload()
 */
export function useR2Upload(options: UseR2UploadOptions = {}) {
  const [isUploading, setIsUploading] = useState(false)
  const auth = useAuth()

  const startUpload = useCallback(
    async (files: File[]): Promise<R2UploadResult[] | undefined> => {
      if (!files || files.length === 0) {
        return undefined
      }

      // 从 useAuth() 直接获取 token
      const token = auth.user?.refresh_token
      if (!token) {
        const error = new Error('未登录：无法获取认证信息')
        options.onUploadError?.(error)
        throw error
      }

      console.log('[R2] 使用 useAuth() 获取到 token，长度:', token.length)

      setIsUploading(true)

      try {
        const results = await uploadToR2(files, token)
        options.onClientUploadComplete?.(results)
        return results
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error))
        options.onUploadError?.(err)
        throw err
      } finally {
        setIsUploading(false)
      }
    },
    [options, auth.user?.refresh_token]
  )

  return {
    startUpload,
    isUploading,
  }
}

/**
 * 简单的上传函数（不使用 Hook）
 * 用于在非组件代码中调用，需要手动传入 token
 */
export async function uploadFiles(
  files: File[],
  token: string
): Promise<{ url: string }[] | undefined> {
  if (!files || files.length === 0) return undefined
  const results = await uploadToR2(files, token)
  return results.map((r) => ({ url: r.url }))
}

